import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ConstructionIcon from "@mui/icons-material/Construction";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { ContactSubmitSchema, type ContactSubmit } from "@corianpark/shared";
import { api } from "../lib/api.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";

const TIERS = [
  {
    name: "Workshop",
    accent: false,
    pitch: "Get on the radar of Aussie operators.",
    features: [
      "Logo on Sources strip",
      "Mention in monthly digest",
      "1 sponsored Dirt Talk per quarter",
    ],
  },
  {
    name: "Yard",
    accent: true,
    pitch: "Headline a week. The whole site sees you.",
    features: [
      "Sponsor a Rig of the Week (logo on hero)",
      "Featured machine slot on Equipment pages",
      "1 sponsored Dirt Talk per month",
      "Quarterly performance report",
    ],
  },
  {
    name: "Fleet",
    accent: false,
    pitch: "Permanent partnership — your brand throughout.",
    features: [
      "Persistent sponsor badge across the site",
      "Co-branded content series",
      "Direct lead routing from Quote requests",
      "Monthly performance call with the team",
    ],
  },
];

export function About() {
  const location = useLocation();

  // Hash-based scroll for /about#contact and /about#companies redirects.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      // Defer so the page is laid out before we scroll
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash, location.pathname]);

  return (
    <>
      <SEO
        title="About Corian Park"
        description="Curated Australian earthmoving content, real operators, real referrals — built by the Corian Park team."
        canonical="https://corianpark.au/about"
      />

      {/* === DREW'S STORY === */}
      <Box sx={{ mb: 6 }}>
        <NeonEyebrow glow>WHO'S BEHIND IT</NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 38, md: 56 },
            fontWeight: 900,
            mt: 1,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
          }}
        >
          Drew Chislett — operator, curator, founder
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 400, maxWidth: 720, lineHeight: 1.5 }}>
          Drew runs Corian Park Earthmoving out of central Victoria. He started Corian Park (the
          platform) because operators kept asking him where to find the good machinery content —
          and the answer was "scattered across forty Facebook groups."
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 3, maxWidth: 720, lineHeight: 1.7 }}>
          From running excavators on real jobs to curating what's worth watching — Corian Park is
          Drew's effort to bring the best of Australian earthmoving into one place. Every Dirt
          Talk card, every Rig of the Week, every quote routed to the right operator: it all goes
          through his eye for what the trade actually cares about.
        </Typography>
      </Box>

      <NeonDivider sx={{ my: 5 }} />

      {/* === WHAT WE DO === */}
      <Box sx={{ mb: 6 }}>
        <NeonEyebrow>WHAT WE DO</NeonEyebrow>
        <Typography variant="h3" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, mt: 1, mb: 3 }}>
          Three jobs, one platform
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              icon: <ConstructionIcon />,
              title: "Curated daily content",
              body: "Videos, articles and Dirt Talk cards from across Australian earthmoving — picked, scored and tagged so you don't have to scroll forever.",
            },
            {
              icon: <LocalShippingIcon />,
              title: "Real operators, real rigs",
              body: "A weekly Rig of the Week, postcode landing pages, and operator profiles. We feature the people actually doing the work.",
            },
            {
              icon: <BusinessIcon />,
              title: "Quote routing",
              body: "Got dirt to move? Send a quote and we route it to an operator with the right gear in your patch. The quote stays between you and them.",
            },
          ].map((card) => (
            <Grid key={card.title} size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ color: "primary.main", mb: 1.5, display: "flex" }}>{card.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>{card.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{card.body}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <NeonDivider sx={{ my: 5 }} />

      {/* === FOR COMPANIES === */}
      <Box id="companies" sx={{ mb: 6, scrollMarginTop: 80 }}>
        <NeonEyebrow glow>FOR COMPANIES</NeonEyebrow>
        <Typography variant="h3" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, mt: 1, mb: 1 }}>
          Reach the people who move the dirt
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
          Brands and dealers that want to support the people running the machines have a few ways
          in — sponsorship, content promotion, and direct lead routing. Set up a Company account
          and we'll get you connected.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
          <Button
            component={RouterLink}
            to="/auth/sign-in?type=company"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<BusinessIcon />}
            sx={{ ...neonButtonSx, fontSize: 16 }}
          >
            Set up a Company account
          </Button>
          <Button
            component="a"
            href="#contact"
            variant="outlined"
            color="primary"
            size="large"
            sx={{ borderColor: "primary.main", color: "primary.main" }}
          >
            Talk to us first
          </Button>
        </Stack>

        <Accordion disableGutters sx={{ bgcolor: "background.paper" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>See sponsorship tiers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {TIERS.map((t) => (
                <Grid key={t.name} size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      p: 2.5,
                      height: "100%",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: t.accent ? "primary.main" : "divider",
                      background: t.accent
                        ? "linear-gradient(180deg, rgba(255,176,0,0.10) 0%, rgba(255,176,0,0.02) 100%)"
                        : undefined,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 900, mb: 1, color: t.accent ? "primary.main" : "text.primary" }}
                    >
                      {t.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t.pitch}
                    </Typography>
                    <Stack spacing={1}>
                      {t.features.map((f) => (
                        <Stack key={f} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
                          <Box sx={{ color: "primary.main", fontWeight: 900, mt: 0.25 }}>✓</Box>
                          <Typography variant="body2">{f}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <NeonDivider sx={{ my: 5 }} />

      {/* === CONTACT === */}
      <Box id="contact" sx={{ scrollMarginTop: 80, mb: 4 }}>
        <NeonEyebrow glow>CONTACT</NeonEyebrow>
        <Typography variant="h3" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, mt: 1, mb: 1 }}>
          Get in touch
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
          Quote requests go through the{" "}
          <RouterLink to="/quote" style={{ color: "inherit" }}>quote form</RouterLink>. For
          sponsorship, partnerships, press, content corrections — drop us a note.
        </Typography>
        <ContactForm />
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 6 }}>
        <Button
          component={RouterLink}
          to="/quote"
          variant="contained"
          color="primary"
          size="large"
          startIcon={<RequestQuoteIcon />}
          sx={{ ...neonButtonSx, fontSize: 16 }}
        >
          Get a quote
        </Button>
        <Button
          component={RouterLink}
          to="/watch"
          variant="outlined"
          color="primary"
          size="large"
          sx={{ borderColor: "primary.main", color: "primary.main" }}
        >
          Browse content
        </Button>
      </Stack>
    </>
  );
}

function ContactForm() {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactSubmit>({
    resolver: zodResolver(ContactSubmitSchema),
    defaultValues: { name: "", email: "", message: "" },
  });
  const [cooldown, setCooldown] = useState(0);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ ok: true }>("/v1/contact", { method: "POST", body: JSON.stringify(body) }),
  });

  const onSubmit = handleSubmit(async (data) => {
    await mutation.mutateAsync(data as unknown as Record<string, unknown>);
    reset();
    setCooldown(60);
    const i = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) { clearInterval(i); return 0; }
        return prev - 1;
      });
    }, 1000);
  });

  if (mutation.isSuccess) {
    return (
      <Alert severity="success" data-testid="contact-success" sx={{ maxWidth: 640 }}>
        Message sent. We'll be in touch shortly.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={onSubmit} noValidate sx={{ maxWidth: 640 }}>
      <Stack spacing={2}>
        {mutation.error && <Alert severity="error">Couldn't send the message. Try again.</Alert>}
        <TextField
          label="Your name"
          {...register("name")}
          error={!!errors.name}
          helperText={errors.name?.message}
          fullWidth
        />
        <TextField
          label="Email"
          type="email"
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
        />
        <TextField
          label="Message"
          multiline
          rows={6}
          {...register("message")}
          error={!!errors.message}
          helperText={errors.message?.message}
          fullWidth
        />
        <Box sx={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden>
          <input type="text" tabIndex={-1} autoComplete="off" {...register("honeypot")} />
        </Box>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={mutation.isPending || cooldown > 0}
          endIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ ...neonButtonSx, alignSelf: "flex-start" }}
        >
          {mutation.isPending ? "Sending…" : cooldown > 0 ? `Wait ${cooldown}s` : "Send message"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Or email{" "}
          <a href="mailto:hello@corianpark.au" style={{ color: "inherit" }}>hello@corianpark.au</a>.
        </Typography>
      </Stack>
    </Box>
  );
}
